import {
  Box,
  Typography,
  useTheme,
  Button,
  Chip,
  Divider,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import PauseCircleOutlinedIcon from "@mui/icons-material/PauseCircleOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useState, useEffect, useCallback } from "react";

const API = "http://64.225.78.248:3001/api/schools";

const StatCard = ({ label, value, color }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  return (
    <Box p="16px" borderRadius="8px" sx={{ backgroundColor: colors.primary[400] }}>
      <Typography variant="h3" fontWeight="bold" color={color}>{value}</Typography>
      <Typography variant="h6" color={colors.grey[300]}>{label}</Typography>
    </Box>
  );
};

const SchoolDetail = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { id } = useParams();
  const navigate = useNavigate();

  const [school, setSchool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const fetchSchool = useCallback(() => {
    setLoading(true);
    fetch(`${API}/${id}`)
      .then((res) => res.json())
      .then((data) => {
        setSchool(data);
        setLoading(false);
      })
      .catch(() => {
        setSnackbar({ open: true, message: "Failed to load school.", severity: "error" });
        setLoading(false);
      });
  }, [id]);

  useEffect(() => {
    fetchSchool();
  }, [fetchSchool]);

  const handleStatusChange = async (newStatus) => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API}/${id}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setSchool(data);
      setSnackbar({ open: true, message: `School ${newStatus.toLowerCase()} successfully.`, severity: "success" });
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: "error" });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API}/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Delete failed");
      setDeleteDialog(false);
      setSnackbar({ open: true, message: "School deleted.", severity: "info" });
      setTimeout(() => navigate("/schools"), 1500);
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: "error" });
    } finally {
      setActionLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(school.onboarding_link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  if (loading) {
    return (
      <Box m="20px" display="flex" justifyContent="center" alignItems="center" height="60vh">
        <CircularProgress sx={{ color: colors.greenAccent[400] }} />
      </Box>
    );
  }

  if (!school || school.error) {
    return (
      <Box m="20px">
        <Typography variant="h4" color={colors.redAccent[400]}>School not found.</Typography>
        <Button onClick={() => navigate("/schools")} sx={{ mt: "12px" }}>Back to Schools</Button>
      </Box>
    );
  }

  const statusConfig = {
    Active: { icon: <CheckCircleOutlinedIcon sx={{ fontSize: "14px" }} />, color: colors.greenAccent[400] },
    Pending: { icon: <PauseCircleOutlinedIcon sx={{ fontSize: "14px" }} />, color: "#f0a500" },
    Suspended: { icon: <PauseCircleOutlinedIcon sx={{ fontSize: "14px" }} />, color: colors.redAccent[400] },
  };

  const { icon, color: statusColor } = statusConfig[school.status] || statusConfig["Pending"];

  return (
    <Box m="20px">
      <Button
        startIcon={<ArrowBackOutlinedIcon />}
        onClick={() => navigate("/schools")}
        sx={{ color: colors.grey[300], mb: "10px" }}
      >
        Back to Schools
      </Button>

      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
        <Header
          title={school.school_name?.toUpperCase()}
          subtitle={`${school.location} · Created ${new Date(school.created_at).toLocaleDateString()}`}
        />
        <Chip
          icon={icon}
          label={school.status}
          sx={{
            backgroundColor: "transparent",
            border: `1px solid ${statusColor}`,
            color: statusColor,
            fontWeight: "bold",
            "& .MuiChip-icon": { color: statusColor },
          }}
        />
      </Box>

      <Box display="grid" gridTemplateColumns="repeat(4, 1fr)" gap="16px" mt="20px" mb="28px">
        <StatCard label="Students" value={school.students_count || 0} color={colors.greenAccent[400]} />
        <StatCard label="Teachers" value={school.teachers_count || 0} color={colors.blueAccent[300]} />
        <StatCard label="Plan" value={school.plan} color="#f0a500" />
        <StatCard label="Status" value={school.status} color={statusColor} />
      </Box>

      <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap="20px" mb="28px">
        <Box p="20px" borderRadius="8px" sx={{ backgroundColor: colors.primary[400] }}>
          <Typography variant="h5" fontWeight="bold" color={colors.grey[100]} mb="16px">School Information</Typography>
          <Divider sx={{ borderColor: colors.grey[700], mb: "16px" }} />
          {[
            { label: "Contact Person", value: school.contact_person },
            { label: "Email", value: school.email },
            { label: "Phone", value: school.phone },
            { label: "Plan", value: school.plan },
            { label: "Database", value: school.db_name },
            { label: "Last Active", value: school.last_active ? new Date(school.last_active).toLocaleDateString() : "Never" },
          ].map(({ label, value }) => (
            <Box key={label} display="flex" justifyContent="space-between" mb="10px">
              <Typography color={colors.grey[400]} variant="h6">{label}</Typography>
              <Typography color={colors.grey[100]} variant="h6" fontWeight="bold"
                sx={{ maxWidth: "60%", textAlign: "right", wordBreak: "break-all" }}>
                {value || "—"}
              </Typography>
            </Box>
          ))}
        </Box>

        <Box p="20px" borderRadius="8px" sx={{ backgroundColor: colors.primary[400] }}>
          <Typography variant="h5" fontWeight="bold" color={colors.grey[100]} mb="16px">Onboarding Link</Typography>
          <Divider sx={{ borderColor: colors.grey[700], mb: "16px" }} />
          <Box p="12px" borderRadius="6px" sx={{ backgroundColor: colors.primary[500] }}>
            <Typography variant="body2" color={colors.blueAccent[300]} sx={{ wordBreak: "break-all", mb: "12px" }}>
              {school.onboarding_link || "No link generated"}
            </Typography>
            <Button
              startIcon={<ContentCopyIcon />}
              onClick={handleCopy}
              size="small"
              sx={{
                color: copied ? colors.greenAccent[400] : colors.grey[300],
                borderColor: copied ? colors.greenAccent[400] : colors.grey[600],
                border: "1px solid",
              }}
            >
              {copied ? "Copied!" : "Copy Link"}
            </Button>
          </Box>

          <Divider sx={{ borderColor: colors.grey[700], my: "20px" }} />
          <Typography variant="h6" color={colors.grey[300]} mb="12px">Actions</Typography>
          <Box display="flex" flexDirection="column" gap="10px">
            <Button
              variant="contained" fullWidth
              disabled={actionLoading || school.status === "Active"}
              onClick={() => handleStatusChange("Active")}
              sx={{ backgroundColor: colors.greenAccent[700], color: "#fff", "&:hover": { backgroundColor: colors.greenAccent[600] }, "&.Mui-disabled": { opacity: 0.4 } }}
            >
              {school.status === "Active" ? "Already Active" : "Activate School"}
            </Button>
            <Button
              variant="outlined" fullWidth
              disabled={actionLoading || school.status === "Suspended"}
              onClick={() => handleStatusChange("Suspended")}
              sx={{ borderColor: "#f0a500", color: "#f0a500", "&:hover": { borderColor: "#f0a500" }, "&.Mui-disabled": { opacity: 0.4 } }}
            >
              {school.status === "Suspended" ? "Already Suspended" : "Suspend School"}
            </Button>
            <Button
              variant="outlined" fullWidth
              disabled={actionLoading}
              onClick={() => setDeleteDialog(true)}
              sx={{ borderColor: colors.redAccent[400], color: colors.redAccent[400], "&:hover": { borderColor: colors.redAccent[300] } }}
            >
              Delete School
            </Button>
          </Box>
        </Box>
      </Box>

      <Dialog
        open={deleteDialog}
        onClose={() => setDeleteDialog(false)}
        PaperProps={{ sx: { backgroundColor: colors.primary[400], color: colors.grey[100] } }}
      >
        <DialogTitle>Delete {school.school_name}?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: colors.grey[300] }}>
            This will permanently remove the school from your platform. The school's database will remain on the server until manually dropped. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)} sx={{ color: colors.grey[300] }}>Cancel</Button>
          <Button onClick={handleDelete} disabled={actionLoading} sx={{ color: colors.redAccent[400], fontWeight: "bold" }}>
            {actionLoading ? "Deleting..." : "Yes, Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={snackbar.severity} sx={{ width: "100%" }}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default SchoolDetail;
